/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { UPDATE_SECTION_OPTION, DELETE_SECTION_OPTION } from "../graphql";
import { CREATE_SECTION_OPTION } from "../graphql/createSectionOption.mutation";
import { VApplication } from "../../../application";

const log: any = LoggerModule?.getInstance();

export interface SectionOptionFormData {
  name?: string;
  code?: string;
  description?: string;
  images?: any[];
  link?: string;
  isMandatory?: boolean;
  isVisible?: boolean;
  isSingleFeature?: boolean;
  presentation?: string;
  section?: string;
  sectionSpecificInvites?: string | number;
  specialInvites?: string | number;
  defaultInvites?: string | number;

  [key: string]: any;
}

export interface CreateSectionOptionProps extends SectionOptionFormData {
  formData?: SectionOptionFormData;
  id?: string;
}

export interface UpdateSectionOptionProps {
  formData: SectionOptionFormData;
  id: string;
  presentation?: string;
  section?: string;
}

export interface DeleteSectionOptionProps {
  id: string;
}

const SECTION_OPTION_FORM_FIELDS = [
  "name",
  "description",
  "code",
  // "link",
  "isMandatory",
  "isVisible",
  "isSingleFeature",
  "sectionSpecificInvites",
  "specialInvites",
  "defaultInvites",
  "images",
];

const SECTION_OPTION_INT_FIELDS = [
  "sectionSpecificInvites",
  "specialInvites",
  "defaultInvites",
];

const SECTION_OPTION_CREATE_PARENT_FIELDS = ["presentation"];
// const SECTION_OPTION_UPDATE_PARENT_FIELDS = ["section"];
const SECTION_OPTION_UPDATE_PARENT_FIELDS: string[] = [];

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const trimIfString = (value: any) => {
  return typeof value === "string" ? value.trim() : value;
};

const normalizeImages = (images: any) => {
  if (!Array.isArray(images)) return images;

  return images.map((image: any) => image?.base64 ?? image).filter(Boolean);
};

const toIntOrSkip = (value: any) => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return undefined;
  }

  return parseInt(String(num), 10);
};

const buildAllowedPayload = (args: any, parentFields: string[]) => {
  const source = {
    ...(args?.formData ?? args ?? {}),
  };

  parentFields.forEach((field) => {
    if (
      (source[field] === undefined ||
        source[field] === null ||
        source[field] === "") &&
      args?.[field] !== undefined
    ) {
      source[field] = args[field];
    }
  });

  const allowedFields = [...SECTION_OPTION_FORM_FIELDS, ...parentFields];

  return allowedFields.reduce((payload: any, field) => {
    if (!Object.prototype.hasOwnProperty.call(source, field)) {
      return payload;
    }

    let value =
      field === "images" ? normalizeImages(source[field]) : source[field];

    if (SECTION_OPTION_INT_FIELDS.includes(field)) {
      value = toIntOrSkip(value);

      if (value === undefined) {
        return payload;
      }
    }

    if (parentFields.includes(field)) {
      if (value !== undefined && value !== null && value !== "") {
        payload[field] = value;
      }

      return payload;
    }

    if (value !== undefined) {
      payload[field] = field === "name" ? trimIfString(value) : value;
    }

    return payload;
  }, {});
};

export interface ISectionOptionMethods extends IPluginMethodMap {
  createSectionOption(args: CreateSectionOptionProps): Promise<any>;
  updateSectionOption(args: UpdateSectionOptionProps): Promise<any>;
  deleteSectionOption(args: DeleteSectionOptionProps): Promise<any>;
}

export class SectionOptionService implements IAgentPlugin {
  readonly methods: ISectionOptionMethods;

  constructor() {
    this.methods = {
      createSectionOption: this.createSectionOption.bind(this),
      updateSectionOption: this.updateSectionOption.bind(this),
      deleteSectionOption: this.deleteSectionOption.bind(this),
    };
  }

  async createSectionOption(args: CreateSectionOptionProps) {
    log?.blue?.(`createSectionOption server component:`, args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const data = buildAllowedPayload(
          args,
          SECTION_OPTION_CREATE_PARENT_FIELDS,
        );

        const res = await client.mutate({
          mutation: CREATE_SECTION_OPTION,
          variables: {
            data,
          },
          fetchPolicy: "no-cache",
        });

        if (res?.data) {
          return {
            ...res?.data?.createCostumeType,
          };
        }
      }
    } catch (err: any) {
      const _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);

      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async updateSectionOption(args: UpdateSectionOptionProps) {
    log?.blue?.(`updateSectionOption server component:`, args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const update = buildAllowedPayload(
          args,
          SECTION_OPTION_UPDATE_PARENT_FIELDS,
        );

        const res = await client.mutate({
          mutation: UPDATE_SECTION_OPTION,
          variables: {
            filter: {
              _id: args?.id,
            },
            update,
          },
          fetchPolicy: "no-cache",
        });

        if (res?.data) {
          return {
            ...res?.data?.updateCostumeType,
          };
        }
      }
    } catch (err: any) {
      const _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);

      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async deleteSectionOption(args: DeleteSectionOptionProps) {
    log?.blue?.(`deleteSectionOption server component:`, args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.mutate({
          mutation: DELETE_SECTION_OPTION,
          variables: {
            filter: {
              _id: args?.id,
            },
          },
          fetchPolicy: "no-cache",
        });

        if (res?.data) {
          return {
            ...res?.data,
          };
        }
      }
    } catch (err: any) {
      const _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);

      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
